<template>
  <div v-if="customer" class="container customerfindnetmatch">
    <div class="row header">
      <div class="col-lg-4 col-xl-4">
        <HomeButton
          v-if="group"
          :group-id="group.id"
        />
        <BackButton />
      </div>
      <div v-if="volunteerSelected" class="col-lg-8 col-xl-9 d-none d-lg-block">
        <h4>{{ $t('customerSearch.customerMatchTitle') }}</h4>
      </div>
    </div>

    <div v-if="group" class="row content">
      <div class="col-lg-4 col-xl-3 mb-3 mb-lg-0 customer-area">
        <CustomerCardNet
          v-model="customer"
          :group="group"
          @refresh="onRefresh"
        />
        <div v-show="isSending" class="loading">
          <div class="spinner">
            <loading />
          </div>
        </div>
      </div>

      <div class="col-lg-8 col-xl-9 volunteer-area">
        <div v-if="!volunteerSelected" class="row volunteer-selection">
          <div class="col-md-4 col-lg-4 order-md-2 search-filter">
            <!--<matchfilter v-model="matchFilter" :is-net="group.netFriendship" @filterchanged="onFilterChanged" />-->
            <customerFilter
              :group="group"
              :filter-type="'VOLUNTEER'"
              :show-all="false"
              @filterchanged="onFilterChanged"
            />
          </div>

          <div class="col-md-8 col-lg-8 order-md-1 volunteer-list">
            <h4>{{ $t('customerSearch.customerMatchTitle') }}</h4>
            <customermatchresults :list="filteredvolunteers" @volunteerSelected="onVolunteerSelected" />
            <div v-show="isLoading" class="loading">
              <div class="spinner">
                <loading />
              </div>
            </div>
            <div v-if="!isLoading && noResults" class="noresults infocontainer">
              <div class="info warning">
                <font-awesome-icon icon="exclamation-triangle" />{{ $t('customerSearch.noResults') }}
              </div>
            </div>
          </div>
        </div>
        <div v-if="volunteerSelected" class="row d-lg-none volunteer-heading">
          <h4>{{ $t('customerSearch.customerMatchTitle') }}</h4>
        </div>
        <div v-if="group && volunteerSelected" class="row ml-lg-1 volunteer-summary">
          <div class="col">
            <VolunteerSummaryNet
              :volunteer="volunteer"
              :customer="customer"
              :group="group"
              :score="score"
              :is-first="isFirst"
              :is-last="isLast"
              :is-inappropriate="isInappropriate"
              @prev="onPrev"
              @next="onNext"
              @close="onClose"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue'
  import { LocaleMessage } from 'vue-i18n'
  import * as _ from 'lodash'
  import { HttpResponse } from 'vue-resource/types/vue_resource'
  import CustomerCardNet from './CustomerCardNet.vue'
  import VolunteerSummaryNet from './VolunteerSummaryNet.vue'
  import customermatchresults from './CustomerMatchResults.vue'
  import BackButton from '../BackButton.vue'
  import HomeButton from '../HomeButton.vue'
  import loading from '../SPRLoading.vue'
  import datehelper from '@/helpers/datehelper'
  import friendMatch from '@/mixins/friendMatch'
  import customerFilter from './CustomerFilter.vue'

  import bitmaskhelper from '@/helpers/bitmaskhelper'

  import { MatchItemNetVolunteerDto, FriendCustomerNetDto, UserProfileDto, FriendManagementDto, FriendshipNetCommandCriteria, GetFriendProfileByFriendVolunteerIdCriteria } from '@/types/index'
  import { MatchFilter, NetMatchPair } from '@/types/custom'

  // the data object
  export interface TemplateComponentData {
    volunteerlist: Array<MatchItemNetVolunteerDto>,
    filteredvolunteers: Array<MatchItemNetVolunteerDto>,
    customer: undefined | FriendCustomerNetDto,
    volunteer: undefined | UserProfileDto,
    score: number,
    isLoading: boolean,
    isSending: boolean,
    isAuthorized: boolean,
    matchFilter: MatchFilter
  }

  export default Vue.extend({
    name: 'CustomerFindNetMatch',
    components: { CustomerCardNet, customerFilter, VolunteerSummaryNet, customermatchresults, BackButton, HomeButton, loading },
    mixins: [friendMatch],
    props: {
      group: {
        type: Object as () => FriendManagementDto,
        required: true
      },
      customerid: {
        type: Number,
        required: true
      }
    },
    data (): TemplateComponentData {
      return {
        volunteerlist: [] as Array<MatchItemNetVolunteerDto>,
        filteredvolunteers: [] as Array<MatchItemNetVolunteerDto>,
        customer: undefined,
        volunteer: undefined,
        score: 0,
        isLoading: false,
        isSending: false,
        isAuthorized: false,
        matchFilter: {
          customerTypes: [],
          languages: [],
          motherTongs: [],
          ages: [],
          hasGroupMembership: false,
          genders: []
        } as MatchFilter
      }
    },
    computed: {
      noResults (): boolean {
        return this.volunteerlist === null || this.filteredvolunteers.length === 0
      },
      isFirst (): boolean {
        if (this.filteredvolunteers.length <= 1) return true
        return this.volunteer
          // @ts-ignore: Object is possibly 'null'.
          ? this.volunteer.friendInfo.id === this.filteredvolunteers[0].match.friendVolunteerId
          : false
      },
      isLast (): boolean {
        if (this.filteredvolunteers.length <= 1) return true
        return this.volunteer
          // @ts-ignore: Object is possibly 'null'.
          ? this.volunteer.friendInfo.id === this.filteredvolunteers[this.filteredvolunteers.length - 1].match.friendVolunteerId
          : false
      },
      isInappropriate (): boolean {
        if (!this.volunteer) return false
        // @ts-ignore: Object is possibly 'null'.
        var selectedVolunteer = this.filteredvolunteers.find(x => x.match.friendVolunteerId === this.volunteer.friendInfo.id)
        return selectedVolunteer ? selectedVolunteer.isInappropriateMatch : false
      },
      hasFriendship (): boolean {
        return this.customer
          // @ts-ignore: Object is possibly 'null'.
          ? this.customer.friendships != null && this.customer.friendships.length > 0
          : false
      },
      volunteerSelected (): boolean {
        return !_.isEmpty(this.volunteer)
      }
    },
    created (): void {
      this.getCustomer()
      this.getVolunteers()
    },
    mounted (): void {
      window.scrollTo(0, 0)

      this.$bus.$on('inappropriate-friendship', this.onInappropriateFriendship)
      this.$bus.$on('end-inappropriate-friendship', this.onEndInappropriateFriendship)
      this.$bus.$on('initiate-friendship', this.onInitiate)
    },
    beforeDestroy (): void {
      this.$bus.$off('inappropriate-friendship')
      this.$bus.$off('end-inappropriate-friendship')
      this.$bus.$off('initiate-friendship')
    },
    methods: {
      getCustomer (): void {
        var self = this
        this.isLoading = true
        var getCustomerUrl = `${this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.customergetnetbyid[0]}`.replace('{customerid}', this.customerid.toString()).replace('{groupid}', this.group.id.toString())

        this.$http.get(getCustomerUrl).then(resp => {
          self.customer = resp.data
          this.isLoading = false
        })
      },
      getVolunteer (friendVolunteerId): void {
        var self = this
        var criteria: GetFriendProfileByFriendVolunteerIdCriteria = {
          friendManagementGroupId: this.group.id,
          friendVolunteerId: friendVolunteerId,
          includeFriendships: true,
          includeFriendPools: false
        }
        var getVolunteerUrl = `${this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.volunteergetnetprofilebyid[0]}`
        this.isLoading = true
        this.$http.post(getVolunteerUrl, criteria).then(resp => {
          if (resp.data) {
            self.volunteer = resp.data
            self.isLoading = false
          } else {
            this.displayError(this.$t('common.fetchFailed'))
          }
        }, f => this.displayError(f))
      },
      getVolunteers (): void {
        var self = this
        this.isLoading = true
        var volunteersUrl = `${this.$store.state.apiBases.friendservice}${this.$store.state.apiEndpoints.customerfindnetmatch[0]}`

        volunteersUrl = volunteersUrl.replace('{customerid}', this.customerid.toString()).replace('{groupid}', this.group.id.toString())
        this.$http.get(volunteersUrl).then(resp => {
          self.volunteerlist = _.reverse(_.sortBy(resp.data.items, ['score']))
          self.filteredvolunteers = self.volunteerlist
          self.isLoading = false
        })
      },
      onVolunteerSelected (match: MatchItemNetVolunteerDto): void {
        window.scrollTo(0, 0)
        // load volunteer from back end
        this.getVolunteer(match.match.friendVolunteerId)
        this.score = match.score
      },
      onInitiate (activation: NetMatchPair): void {
        var data: FriendshipNetCommandCriteria = {
          customerProfileNetId: activation.customer.id,
          volunteerNetProfileId: activation.volunteer.friendInfo.netProfile.id,
          groupId: this.group.id
        }
        var url = `${this.$store.state.apiBases.friendservice}${this.$store.state.apiEndpoints.friendshipinitiatenet[0]}`
        this.$http.post(url, data).then(resp => {
          this.$router.push(`/friend/customermatch/${this.group.id}/${this.customer?.id}/${activation.volunteer.friendInfo.id}`)
        }, f => this.displayError(f || this.$t('common.fetchFailed')))
      },
      onPrev (): void {
        var currentVolunteer = _.findIndex(this.filteredvolunteers, (item: MatchItemNetVolunteerDto) => {
          if (!this.volunteer) return false
          return item.match.friendVolunteerId === this.volunteer.friendInfo.id
        })

        if (currentVolunteer > 0) {
          var id = this.filteredvolunteers[currentVolunteer - 1].match.friendVolunteerId
          this.getVolunteer(id)
          this.score = this.filteredvolunteers[currentVolunteer - 1].score
        }
      },
      onNext (): void {
        var currentVolunteer = _.findIndex(this.filteredvolunteers, (item: MatchItemNetVolunteerDto) => {
          if (!this.volunteer) return false
          return item.match.friendVolunteerId === this.volunteer.friendInfo.id
        })

        if (currentVolunteer > -1 && currentVolunteer < this.filteredvolunteers.length - 1) {
          this.getVolunteer(this.filteredvolunteers[currentVolunteer + 1].match.friendVolunteerId)
          this.score = this.filteredvolunteers[currentVolunteer + 1].score
        }
      },
      onClose (): void {
        this.volunteer = undefined
      },
      onInappropriateFriendship (friendshipInfo: NetMatchPair): void {
        var data: FriendshipNetCommandCriteria = {
          customerProfileNetId: friendshipInfo.customer.id,
          volunteerNetProfileId: friendshipInfo.volunteer.friendInfo.netProfile.id,
          groupId: this.group.id
        }
        var url = `${this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.friendshipmarkinappropriatenet[0]}`
        this.$http.post(url, data).then((resp) => {
          this.volunteer = undefined
          this.getVolunteers()
        })
      },
      onEndInappropriateFriendship (friendshipInfo: NetMatchPair): void {
        var data: FriendshipNetCommandCriteria = {
          customerProfileNetId: friendshipInfo.customer.id,
          volunteerNetProfileId: friendshipInfo.volunteer.friendInfo.netProfile.id,
          groupId: this.group.id
        }
        var url = `${this.$store.state.apiBases.friendservice + this.$store.state.apiEndpoints.friendshipendinappropriatenet[0]}`
        this.isLoading = true
        this.$http.post(url, data).then((resp) => {
          this.volunteer = undefined
          this.getVolunteers()
        }, f => {
          // @ts-ignore Doesnt pick up mixins
          this.showAlertError(this.$t('common.fetchFailed'))
        })
      },
      onFilterChanged (val: MatchFilter): void {
        var self = this
        this.matchFilter = val
        this.filteredvolunteers = this.volunteerlist.filter((matchresult: MatchItemNetVolunteerDto) => {
          return (!self.matchFilter.customerTypes?.length || bitmaskhelper.bitmaskToArray(matchresult.match.customerTypes).some(customerType => self.matchFilter.customerTypes?.includes(customerType))) &&
            (!self.matchFilter.languages?.length || matchresult.match.spokenLanguages.some(spokenLanguage => self.matchFilter.languages?.includes(spokenLanguage))) &&
            (!self.matchFilter.motherTongs?.length || self.matchFilter.motherTongs?.includes(matchresult.match.nativeLanguage)) &&
            (!self.matchFilter.ages?.length || self.matchFilter.ages?.includes(friendMatch.methods.calculateBitmaskFromAge(datehelper.calculateAge(matchresult.match.birthDate))))
        })
      },
      displayError (err: Response | any): void {
        if (Array.isArray(err.body)) {
          err.body.forEach(x => {
            // @ts-ignore
            this.showAlertError(x.errorMessage)
          })
        } else if (err.body && err.body.errorMessage) {
          // @ts-ignore
          this.showAlertError(err.errorMessage)
        } else if (err.bodyText) {
          // @ts-ignore
          this.showAlertError(err.bodyText)
        } else {
          // @ts-ignore
          this.showAlertError(this.$t('common.fetchFailed'))
        }
      },
      onRefresh (): void {
        window.location.reload()
      }
    }
  })
</script>

<style lang="scss">
  @import "../../assets/scss/_variables.scss";

  .customerfindnetmatch {
    .header {
      margin-top: 10px;
      margin-bottom: 15px;
      .nav-header {
        border-bottom: 0;
      }
      h4 {
        padding-left: 10px;
      }
    }
    .content {

      .customer-area {
        padding-top: 30px;
        background-color: $brand-white;
        border-left: solid 5px $customer-color-strong;
      }

      .volunteer-area {
        .volunteer-selection {
          height: 100%;
          .search-filter {
            background-color:$brand-white;
            padding-top: 20px;
          }
          .volunteer-list {
            h4 {
              margin: 20px 0 10px 0;
            }
            .loading {
              position: absolute;
              display: flex;
              top: 0;
              left: 0;
              bottom: 0;
              right: 0;
              height: 100%;
              width: 100%;
              background-color:rgba(255, 255, 255, 0.8);
              .spinner {
                margin: auto;
                color:$brand-red;
              }
            }
            .infocontainer {
              height: 200px;
              width: 100%;
              background-color:$brand-white;
              display: flex;
              justify-content: center;
              align-items:center;
              align-content: center;
              padding:50px;
              .info {
                margin: auto;
                svg {
                  margin-right: 5px;
                  color:$brand-red;
                  &.warning {
                    color:$brand-warning;
                  }
                }
              }
            }
          }
        }
        .volunteer-heading {
          h4 {
            padding-left: 10px;
          }
        }
        .volunteer-summary {
          @media (min-width: $media-lg) {
            height: 100%;
          }
        }
      }

    }
  }
</style>
